import { useMemo } from 'react';

import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

const defaultRestorableSelector = () => undefined;

/**
 * Creates a hook that returns a holder for either a user selected value or a default
 */
export const createSelectedHook = <SelectedType, ApiHookResponse, RestorableType>(createSelectedHookOptions: {
    /**
     * Selector to get the user selected value
     */
    selector: (state: RootState) => SelectedType;
    /**
     * Selector to get the restorable value
     */
    restorableSelector?: (state: RootState) => RestorableType;
    /**
     * Hook to fetch api data
     */
    apiQueryHook: () => ApiHookResponse;
    /**
     * Callback that determines whether the default value should be returned instead of the user selected value
     */
    shouldReturnDefault?: (arg: { selectedValue: SelectedType; apiHookResponse: ApiHookResponse }) => boolean;
    /**
     * Callback to get the default value from the {@link selectedValue} and {@link apiHookResponse}
     */
    selectDefaultValue?: (arg: { selectedValue: SelectedType; apiHookResponse: ApiHookResponse }) => SelectedType;
    /**
     * Callback to decide how to best use the restorableValue - returns an actual selection record, not true/false
     */
    selectRestoredValue?: (arg: {
        restorableValue: RestorableType;
        selectedValue: SelectedType;
        apiHookResponse: ApiHookResponse;
    }) => SelectedType;
}): (() => { selectedValue: SelectedType; isDefault: boolean }) => {
    return () => {
        const {
            selector,
            restorableSelector = defaultRestorableSelector,
            apiQueryHook,
            shouldReturnDefault = () => false,
            selectDefaultValue = (arg) => arg.selectedValue,
            selectRestoredValue = (_arg) => undefined,
        } = createSelectedHookOptions;

        const selectedValue = useAppSelector(selector);
        const apiHookResponse = apiQueryHook();

        const restorableValue = useAppSelector(restorableSelector);

        const restoredValue = useMemo(() => {
            return restorableValue
                ? selectRestoredValue({
                      restorableValue,
                      selectedValue,
                      apiHookResponse,
                  })
                : undefined;
        }, [apiHookResponse, restorableValue, selectRestoredValue, selectedValue]);

        const _shouldReturnDefault = useMemo(
            () => shouldReturnDefault({ selectedValue, apiHookResponse }),
            [apiHookResponse, selectedValue, shouldReturnDefault]
        );
        const defaultValue = useMemo(
            () => selectDefaultValue({ selectedValue, apiHookResponse }),
            [apiHookResponse, selectDefaultValue, selectedValue]
        );

        return {
            selectedValue: _shouldReturnDefault ? restoredValue ?? defaultValue : selectedValue,
            isDefault: _shouldReturnDefault,
        };
    };
};
